class Hit {
  constructor(hitType, hit) {
    this.hitType = hitType;
    this.hit = hit;
  }

  /**
   * Convert a string to a safe HTML free string with decoded entities.
   *
   * @param {String} html
   * @returns {String}
   */
  decodeHtml(html) {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  }

  /**
   * Return the template depending on the post type of the hit.
   */
  template(html) {
    switch (this.hitType) {
      case 'person':
        return this.personHit(html);
      case 'post':
        return this.newsHit(html);
      case 'page':
        return this.pageHit(html);
      case 'event':
        return this.eventHit(html);
      case 'degree':
        return this.degreeHit(html);
      default:
        return `I am a ${this.hitType} that needs a hit template.`;
    }
  }

  /**
   * Person hit.
   */
  personHit(html) {
    const { departments, email, location, phone, title, titles, thumbnail, url, link, website } =
      this.hit;
    const profileLink = link || url;
    return html` <div
      class="person-hit ${thumbnail && thumbnail.src ? 'person-hit--has-image' : ''}">
      ${thumbnail && thumbnail.src
    ? html` <div class="person-hit__art has__art-link">
            <figure class="person-hit__figure">
              <img src="${thumbnail.src}" alt="${thumbnail.alt}" class="person-hit__img" />
            </figure>
            <a class="person-hit__link" href="${profileLink}" aria-hidden="true" tabindex="-1"></a>
          </div>`
    : ''}
      <div class="person-hit__titles">
        <h2 class="person-hit__name">
          <a class="person-hit__link" href="${profileLink}">${title}</a>
        </h2>
        ${titles
    ? html`<p class="person-hit__title">
              ${titles.map(title => html`<span>${title}</span>`)}
            </p>`
    : ''}
        ${departments
    ? html` <ul class="person-hit__departments">
              ${departments.map(
    department => html` <li class="person-hit__departments-item">
                  <a class="person-hit__departments-text" href=${department.link}
                    >${department.name}</a
                  >
                </li>`,
  )}
            </ul>`
    : ''}
      </div>
      ${email || website || phone || location
    ? html`<div class="person-hit__contact">
            <ul class="person-hit__contact-list">
              ${email
    ? html`<li class="person-hit__contact-info">
                    <a href="mailto:${email}">${email}</a>
                  </li>`
    : ''}
              ${website
    ? html`<li class="person-hit__contact-info">
                    <a href="${website.url}">${website.title || website.url}</a>
                  </li>`
    : ''}
              ${phone ? html`<li class="person-hit__contact-info">${phone}</li>` : ''}
              ${location ? html`<li class="person-hit__contact-info">${location}</li>` : ''}
            </ul>
          </div>`
    : ''}
      <div class="person-hit__button">
        <a class="person-hit__button-link" href="${profileLink}" aria-hidden="true" tabindex="-1"
          >View profile</a
        >
      </div>
    </div>`;
  }

  /**
   * News hit.
   */
  newsHit(html) {
    const { description, link, title, thumbnail, blogname, blogID } = this.hit;
    return html` <div class="hit ${thumbnail && thumbnail.src ? 'hit--has-image' : ''}">
      ${thumbnail && thumbnail.src
    ? html` <div class="hit__art has__art-link">
            <figure class="hit__figure">
              <img src="${thumbnail.src}" alt="${thumbnail.alt}" class="hit__img" />
            </figure>
            <a class="hit__link" href="${link}" aria-hidden="true" tabindex="-1"></a>
          </div>`
    : ''}
      <div class="hit__text">
        <h2 class="hit__name">
          <a class="hit__link" href="${link}">${title}</a>
        </h2>
        ${blogID !== 1 && html`<p class="hit__source">${blogname}</p>`}

        <p class="hit__excerpt">${this.decodeHtml(description)}</p>
      </div>
      <div class="hit__button">
        <a class="hit__button-link" href="${link}" aria-hidden="true" tabindex="-1">View article</a>
      </div>
    </div>`;
  }

  /**
   * Page hit.
   */
  pageHit(html) {
    const { link, title, thumbnail, blogname, description } = this.hit;
    return html` <div class="hit ${thumbnail && thumbnail.src ? 'hit--has-image' : ''}">
      ${thumbnail && thumbnail.src
    ? html` <div class="hit__art has__art-link">
            <figure class="hit__figure">
              <img src="${thumbnail.src}" alt="${thumbnail.alt}" class="hit__img" />
            </figure>
            <a class="hit__link" href="${link}" aria-hidden="true" tabindex="-1"></a>
          </div>`
    : ''}
      <div class="hit__text">
        <h2 class="hit__name">
          <a class="hit__link" href="${link}">${title}</a>
        </h2>
        <p class="hit__source">${blogname}</p>

        <p class="hit__excerpt">${this.decodeHtml(description)}</p>
      </div>
      <div class="hit__button">
        <a class="hit__button-link" href="${link}" aria-hidden="true" tabindex="-1">View page</a>
      </div>
    </div>`;
  }

  /**
   * Event hit.
   */
  eventHit(html) {
    const { date_time, link, title, thumbnail } = this.hit;
    return html` <div class="hit ${thumbnail && thumbnail.src ? 'hit--has-image' : ''}">
      ${thumbnail && thumbnail.src
    ? html` <div class="hit__art has__art-link">
            <figure class="hit__figure">
              <img src="${thumbnail.src}" alt="${thumbnail.alt}" class="hit__img" />
            </figure>
            <a class="hit__link" href="${link}" aria-hidden="true" tabindex="-1"></a>
          </div>`
    : ''}
      <div class="hit__text">
        <h2 class="hit__name">
          <a class="hit__link" href="${link}">${title}</a>
        </h2>
        <p class="event-topper__detail-info">
          <time
            >${date_time.date}${date_time.time ? html`<span>${date_time.time}</span>` : ''}</time
          >
        </p>
      </div>
      <div class="hit__button">
        <a class="hit__button-link" href="${link}" aria-hidden="true" tabindex="-1">View event</a>
      </div>
    </div>`;
  }

  /**
   * Degree hit.
   */
  degreeHit(html) {
    const { description, title, thumbnail, url, blogname, degree_type } = this.hit;
    return html` <div class="hit ${thumbnail && thumbnail.src ? 'hit--has-image' : ''}">
      ${thumbnail && thumbnail.src
    ? html` <div class="hit__art has__art-link">
            <figure class="hit__figure">
              <img src="${thumbnail.src}" alt="${thumbnail.alt}" class="hit__img" />
            </figure>
            <a class="hit__link" href="${url}" aria-hidden="true" tabindex="-1"></a>
          </div>`
    : ''}
      <div class="hit__text">
        <h2 class="hit__name">
          <a class="hit__link" href="${url}">${title}</a>
        </h2>
        <p class="hit__source">${blogname}</p>

        ${degree_type?.abbrev ? html`<p class="degee-hit__type">${degree_type.abbrev}</p>` : ''}
        <p class="hit__excerpt">${this.decodeHtml(description)}</p>
      </div>
      <div class="hit__button">
        <a class="hit__button-link" href="${url}" aria-hidden="true" tabindex="-1">View degree</a>
      </div>
    </div>`;
  }
}

export default Hit;
